import { useCycleStore } from '@cycle-hangars/store/store';
import {
  assetTypeId,
  CycleHangarsEstatesFeatureAttributes,
  CycleHangarsFeatureAttributes,
} from '@types';

export interface PayloadObject {
  ReportObject: ReportObject;
  ServiceLocationObject: ServiceLocationObject;
  ChannelObject: ChannelObject;
  ReporterObject: ReporterObject;
  FileCollectionObject: FileCollectionObject | null | undefined;
}

export interface ReportObject {
  issue_type: string;
  hangar_secure: string;
  key_code_match: boolean;
  key_operates: boolean;
  have_lock_code: boolean;
  lock_code: string;
  more_details: string;
}

export interface ServiceLocationObject {
  address_line_1: string;
  address_line_2: string;
  town_city: string;
  county: string;
  postcode: string;
  country: string;
  UPRN: string;
  USRN: string;
  latitude: number;
  longitude: number;
  easting: string;
  northing: string;
  additional_details: string;
  asset: Asset;
}

export interface Asset {
  asset_id: string;
  layer_id: number;
}

export interface ChannelObject {
  channel: string;
  platform: string;
  session_id: string;
}

export interface ReporterObject {
  can_be_contacted: boolean;
  can_back_office_contact: boolean;
  contact_id: string | null;
  name: Name;
  contact_details: ContactDetails | null;
}

export interface Name {
  first_name: string;
  middle_name: string;
  last_name: string;
}

export interface ContactDetails {
  email_address: string;
  phone_number: string;
}

export interface FileCollectionObject {
  external: boolean;
  files: File[];
}

export interface File {
  name: string;
  path: string;
}

export const CycleHangarPayload = (): PayloadObject => {
  const {
    firstName,
    lastName,
    email,
    serviceLocationDetails,
    tellUsMoreText,
    uploadedImagesBlob,
    receiveConfirmationEmail,
    sessionId,
    cycleHangarIssue,
    isSecured,
    doesCodeMatch,
    doesKeyTurn,
    lockCode,
    knowsLockCode,
    serviceAssetDetails,
    cleaningTellUsMoreText,
    vandalismTellUsMoreText,
    describeBikeText,
    abandondedTellUsMoreText,
    selectedDate,
    cycleHangarId,
  } = useCycleStore();

  const {
    address_line_1,
    address_line_2,
    town_city,
    county,
    postcode,
    country,
    UPRN,
    USRN,
    latitude,
    longitude,
    easting,
    northing,
  } = serviceLocationDetails;

  let uploadedFiles: { name: string; path: string }[] = [];
  uploadedImagesBlob?.forEach((file) => {
    uploadedFiles.push({
      name: file.filename,
      path: file.blob_uri,
    });
  });

  const payload = {
    ReportObject: {
      issue_type: cycleHangarIssue,
      hangar_secure: isSecured,
      key_code_match: doesCodeMatch === 'Yes',
      key_operates: doesKeyTurn === 'Yes',
      have_lock_code: knowsLockCode === 'Yes',
      lock_code: lockCode,
      incident_date: new Date(
        Number(selectedDate.selectedYear),
        Number(selectedDate.selectedMonth) - 1, // month is 0 based
        Number(selectedDate.selectedDay),
        12,
        0,
        0, // Set time to 12:00:00 to avoid timezone issues
      ).toISOString(),
      cycle_hangar_issue_details:
        cleaningTellUsMoreText || vandalismTellUsMoreText || describeBikeText,
      space_in_hangar: abandondedTellUsMoreText,
      more_details: tellUsMoreText,
    },
    ServiceLocationObject: {
      address_line_1,
      address_line_2,
      town_city,
      county,
      postcode,
      country,
      UPRN,
      USRN,
      easting,
      northing,
      latitude,
      longitude,
      additional_details: '',
      asset: {
        asset_id:
          (serviceAssetDetails as CycleHangarsFeatureAttributes)?.CH_Ref ||
          (serviceAssetDetails as CycleHangarsEstatesFeatureAttributes)
            ?.Unit_ID ||
          cycleHangarId,
        layer_id:
          (serviceAssetDetails as CycleHangarsFeatureAttributes)?.CH_Ref !==
          undefined
            ? Number.parseInt(assetTypeId.CycleHangars)
            : Number.parseInt(assetTypeId.CycleHangarsEstates),
      },
    },
    ChannelObject: {
      channel: 'Online',
      platform: 'React',
      session_id: sessionId,
    },
    ReporterObject: {
      can_be_contacted: receiveConfirmationEmail === 'Yes',
      can_back_office_contact: false,
      contact_id: null,
      name: {
        first_name: firstName,
        middle_name: '',
        last_name: lastName,
      },
      contact_details: {
        email_address: email,
        phone_number: '',
      },
    },
    ...(uploadedFiles?.length > 0 && {
      FileCollectionObject: {
        external: false,
        files: uploadedFiles,
      },
    }),
  };

  return payload as PayloadObject;
};
